import React from 'react';
import toast from 'react-hot-toast';

/**
 * Configuración de duraciones para los diferentes tipos de alertas
 */
const DURATIONS = {
  success: 5000,
  error: 5000,
  info: 8000,
  warning: 6000,
  loading: Infinity,
};

/**
 * Muestra una alerta de éxito
 * @param message - Mensaje a mostrar
 * @param duration - Duración en milisegundos (por defecto 5000ms)
 */
export const showSuccess = (message: string, duration?: number): string => {
  return toast.success(message, {
    duration: duration ?? DURATIONS.success,
  });
};

/**
 * Muestra una alerta de error
 * @param message - Mensaje a mostrar
 * @param duration - Duración en milisegundos (por defecto 5000ms)
 */
export const showError = (message: string, duration?: number): string => {
  return toast.error(message, {
    duration: duration ?? DURATIONS.error,
  });
};

/**
 * Muestra una alerta informativa
 * @param message - Mensaje a mostrar
 * @param duration - Duración en milisegundos (por defecto 8000ms)
 */
export const showInfo = (message: string, duration?: number): string => {
  return toast(message, {
    icon: (
      <svg
        className="shrink-0"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    ),
    duration: duration ?? DURATIONS.info,
    style: {
      background: '#eff6ff',
      color: '#1e40af',
      border: '1px solid #bfdbfe',
      padding: '16px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
  });
};

/**
 * Muestra una alerta de advertencia
 * @param title - Título de la advertencia
 * @param message - Mensaje descriptivo (opcional)
 * @param duration - Duración en milisegundos (por defecto 6000ms)
 */
export const showWarning = (
  title: string,
  message?: string,
  duration?: number,
): string => {
  return toast(
    (t) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' }}>
        <svg
          style={{ flexShrink: 0, marginTop: '2px' }}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#854d0e"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: '#854d0e',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: message ? '4px' : 0,
            }}
          >
            {title}
          </div>
          {message && (
            <div
              style={{
                color: '#a16207',
                fontSize: '14px',
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    ),
    {
      duration: duration ?? DURATIONS.warning,
      style: {
        background: '#fefce8',
        border: '1px solid #fde68a',
        padding: '16px',
        borderRadius: '8px',
        maxWidth: '420px',
      },
    },
  );
};

/**
 * Muestra una alerta de carga
 * @param message - Mensaje a mostrar
 */
export const showLoading = (message: string): string => {
  return toast.loading(message);
};

/**
 * Actualiza una alerta existente a éxito
 * @param toastId - ID del toast a actualizar
 * @param message - Nuevo mensaje
 * @param duration - Duración en milisegundos (por defecto 5000ms)
 */
export const updateToSuccess = (
  toastId: string,
  message: string,
  duration?: number,
): void => {
  toast.success(message, {
    id: toastId,
    duration: duration ?? DURATIONS.success,
  });
};

/**
 * Actualiza una alerta existente a error
 * @param toastId - ID del toast a actualizar
 * @param message - Nuevo mensaje
 * @param duration - Duración en milisegundos (por defecto 5000ms)
 */
export const updateToError = (
  toastId: string,
  message: string,
  duration?: number,
): void => {
  toast.error(message, {
    id: toastId,
    duration: duration ?? DURATIONS.error,
  });
};

/**
 * Maneja una promesa con alertas automáticas
 * @param promise - Promesa a manejar
 * @param messages - Mensajes para cada estado
 * @param options - Opciones de duración personalizadas
 */
export const handlePromise = <T,>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string;
    error: string;
  },
  options?: {
    successDuration?: number;
    errorDuration?: number;
  },
): Promise<T> => {
  return toast.promise(promise, messages, {
    success: {
      duration: options?.successDuration ?? DURATIONS.success,
    },
    error: {
      duration: options?.errorDuration ?? DURATIONS.error,
    },
  });
};

/**
 * Cierra una alerta específica
 * @param toastId - ID del toast a cerrar
 */
export const dismissToast = (toastId: string): void => {
  toast.dismiss(toastId);
};

/**
 * Cierra todas las alertas activas
 */
export const dismissAll = (): void => {
  toast.dismiss();
};

/**
 * Hook para operaciones asíncronas con feedback visual
 * Útil para operaciones CRUD
 * @param operation - Función asíncrona a ejecutar
 * @param messages - Mensajes personalizados (opcional)
 */
export const withAsyncFeedback = async <T,>(
  operation: () => Promise<T>,
  messages?: {
    loading?: string;
    success?: string;
    error?: string;
  },
): Promise<T> => {
  const loadingToast = showLoading(messages?.loading ?? 'Procesando...');

  try {
    const result = await operation();
    updateToSuccess(
      loadingToast,
      messages?.success ?? '¡Operación completada exitosamente!',
    );
    return result;
  } catch (error) {
    updateToError(
      loadingToast,
      messages?.error ?? 'Ocurrió un error en la operación',
    );
    throw error;
  }
};

// Exportar el toast original para casos de uso avanzados
export { toast };
